/* eslint-disable react/jsx-no-useless-fragment */
import { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { selectRootLoading, selectUserData } from 'common/store/selectors';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
} from '@mui/material';
import { TermsModal } from 'common/ui/terms-modal';
import { ulid } from 'ulid';
import { useSearchParams } from 'react-router-dom';
import { SignUpContent } from './components';
import { IAsinData } from './components/payment-information/payment-information';
import { CompanyAgreement, CompanyAgreementMap } from './constants';
import { initWithHash, setLoaderActiveAction, setLoaderInactiveAction } from '../../common/store/actions';
import { BrandsHub } from '../../common/constants/brands-hub';

function LoadingContainer({ children, loading }: PropsWithChildren<{ loading: boolean }>) {
  return (
    <>
      {children}
      {loading && (
        <Box sx={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}>
          <LinearProgress />
          <Box sx={{ width: '100%', height: '100%', backgroundColor: 'white', zIndex: 10000, opacity: 0.5 }} />
        </Box>
      )}
    </>
  );
}

export interface SignUpFormValues {
  firstName: string;
  lastName: string;
  companyName: string;
  address: string;
  email: string;
  phone: string;
  password: string;
  country: string;
  domain: string;
  isAffiliateContent: boolean;
  isBrand: boolean;
  sign: File;
  orderList: string;
  payment: string;
  cardName: string;
  cardNumber: string;
  expiration: string;
  cvv: string;
  arContactFirstName: string;
  arContactLastName: string;
  arContactEmail: string;
  acctContactFirstName: string;
  acctContactLastName: string;
  acctContactEmail: string;
  socials?: { facebook: string; instagram: string; tiktok: string; twitter: string; youtube: string };
  facebook: string;
  instagram: string;
  tiktok: string;
  twitter: string;
  youtube: string;
  confirmPassword: string;
  summaryService: boolean;
  termsAndConditionals: boolean;
  orders: IAsinData;
  gainzActive: boolean;
}

const passwordSchema = Yup.string()
  .required('Password is required')
  .min(8, 'Password must be at least 8 characters')
  .matches(/[a-z]/, 'Password must contain a lowercase letter')
  .matches(/[A-Z]/, 'Password must contain an uppercase letter')
  .matches(/[0-9]/, 'Password must contain a number')
  .matches(/[!@#$%^&*]/, 'Password must contain a special character (!@#$%^&*)')
  .test(
    'no-3-identical-chars',
    'Password cannot contain more than 2 identical characters in a row',
    value => !/(.)\1\1/.test(value || '')
  );

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is a required field'),
  lastName: Yup.string().required('Last name is a required field'),
  companyName: Yup.string().when('isBrand', {
    is: true,
    then: schema => schema.required('Brand name is required'),
    otherwise: schema => schema.required('Creator name is a required field'),
  }),
  address: Yup.string().required('Address is a required field'),
  email: Yup.string().required('Email is a required field').email('Enter a valid email address'),
  phone: Yup.string(),
  isBrand: Yup.boolean(),
  domain: Yup.string().when('isBrand', {
    is: true,
    then: schema =>
      schema
        .required('Store URL is required')
        .matches(/amazon\.com\/stores\/./, 'Store URL should be like amazon.com/stores/test'),
    otherwise: schema =>
      schema.matches(/^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/, 'Enter a valid domain (e.g. mydomain.com)'),
  }),
  country: Yup.string().required('Country is a required field').oneOf(['GB', 'US'], 'Only UK and US are supported'),
  password: passwordSchema,
  confirmPassword: Yup.string()
    .required('Confirm password is a required field')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
  isAffiliateContent: Yup.string().required(),
  orders: Yup.array().of(
    Yup.object({
      asin: Yup.string().min(3).required('ASIN is required field'),
      category: Yup.string().required(),
      budget: Yup.number().min(500).max(5000).required('Budget should be in range'),
    })
  ),
});

const styles = {
  rootBox: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: 500,
    minWidth: 350,
    px: 4,
    margin: '0 auto',
  },
  stepBox: (active: boolean) => ({
    width: 40,
    height: 6,
    borderRadius: 3,
    backgroundColor: active ? 'primary.main' : 'grey.300',
    transition: 'background 0.2s',
  }),
  dialogTitle: { fontWeight: 700, fontSize: 20, textAlign: 'center' },
  dialogText: { mb: 2, fontSize: 16 },
  dialogButton: { borderRadius: 2, minWidth: 100 },
};

export function SignUp(): ReactElement {
  const loading = useSelector(selectRootLoading);
  const dispatch = useDispatch();
  const user = useSelector(selectUserData);
  const [searchParams] = useSearchParams();
  const uncompletedEmail = searchParams.get('uncompleted_onboarding');
  const brandsHub = searchParams.get('brands_hub') as BrandsHub;

  const [step, setStep] = useState<number>(1);
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [showPrivacy, setShowPrivacy] = useState<boolean>(false);
  const [socialError, setSocialError] = useState<string | null>('unexpected error');
  const [showSocialErrorModal, setShowSocialErrorModal] = useState(false);

  const getInitialValues: SignUpFormValues = {
    firstName: user?.first_name ?? '',
    lastName: user?.last_name ?? '',
    address: user?.address ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    password: '',
    confirmPassword: '',
    companyName: user?.company_name ?? '',
    country: user?.country ?? '',
    domain: user?.domain ?? '',
    isAffiliateContent: user?.is_affiliate_content ?? false,
    isBrand: user?.is_brand ?? true,
    sign: null,
    orderList: '',
    payment: '',
    cardName: '',
    cardNumber: '',
    expiration: '',
    cvv: '',
    arContactFirstName: user?.ar_contact_first_name ?? '',
    arContactLastName: user?.ar_contact_last_name ?? '',
    arContactEmail: user?.ar_contact_email ?? '',
    acctContactFirstName: user?.acct_contact_first_name ?? '',
    acctContactLastName: user?.acct_contact_last_name ?? '',
    acctContactEmail: user?.acct_contact_email ?? '',
    facebook: user?.socials?.facebook ?? '',
    instagram: user?.socials?.instagram ?? '',
    tiktok: user?.socials?.tiktok ?? '',
    twitter: user?.socials?.twitter ?? '',
    youtube: user?.socials?.youtube ?? '',
    summaryService: true,
    termsAndConditionals: true,
    orders: null,
    gainzActive: true,
  };

  const openTermsModal = () => setShowTerms(true);
  const closeTermsModal = () => setShowTerms(false);
  const openServiceModal = () => setShowPrivacy(true);
  const closePrivacyModal = () => setShowPrivacy(false);

  const handleFormSubmit = () => {
    //
  };

  const onSuccess = () => {
    dispatch(setLoaderInactiveAction());
    setStep((prev: number) => prev + 1);
  };

  useEffect(() => {
    if (uncompletedEmail && brandsHub) {
      dispatch(setLoaderActiveAction());
      const hash = sessionStorage.getItem('hash') || ulid();
      dispatch(
        initWithHash(
          hash,
          uncompletedEmail,
          brandsHub,
          onSuccess,
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          () => {}
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoadingContainer loading={loading}>
      <Box sx={styles.rootBox}>
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={getInitialValues}
          validationSchema={validationSchema}
          validateOnChange
          validateOnMount
        >
          <>
            <SignUpContent
              step={step}
              setStep={setStep}
              openTermsModal={openTermsModal}
              openServiceModal={openServiceModal}
              setSocialError={setSocialError}
              showSocialSignUpErrorDialog={() => setShowSocialErrorModal(true)}
            />
          </>
        </Formik>
      </Box>
      <TermsModal
        open={showTerms}
        onClose={closeTermsModal}
        src={CompanyAgreementMap[CompanyAgreement.TermsAndConditions].link}
        title="Terms and Conditions"
      />
      ;
      <TermsModal
        open={showPrivacy}
        onClose={closePrivacyModal}
        src={CompanyAgreementMap[CompanyAgreement.ServiceSummary].link}
        title="Service Summary"
      />
      ;
      <Dialog
        open={showSocialErrorModal}
        onClose={() => setShowSocialErrorModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            minWidth: 340,
            maxWidth: 400,
            boxShadow: 6,
          },
        }}
      >
        <DialogTitle sx={styles.dialogTitle}>Social Sign Up Error</DialogTitle>
        <DialogContent>
          <Typography sx={styles.dialogText}>{socialError}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSocialErrorModal(false)} sx={styles.dialogButton}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </LoadingContainer>
  );
}
